import { LatLngExpression } from 'leaflet';
import moment from 'moment';
import { MultiPolygon, Polygon } from 'geojson';
import { kml } from '@tmcw/togeojson';
import { toKML } from '@placemarkio/tokml';
import { User } from '../models/organizationModels';

export function convertToDate(dateStr: string) {
    return moment(dateStr, 'YYYY-MM-DD').toDate();
}

export function dateToString(date: Date) {
    return date.toISOString().split('T')[0];
}

export function formatDate(date: string) {
    return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
}

export function formatTime(date: string) {
    return moment(date, 'hh:mm:ss').format('hh:mm:ss');
}

export function formatDateTime(date: string) {
    return moment(date, 'YYYY-MM-DD hh:mm:ss').format('YYYY-MM-DD hh:mm:ss');
}

export function isNumericReal(str: string) {
    return /^\d*(\.\d+)?$/.test(str);
}
export function isNumeric(str: string) {
    return /^-?[0-9]+$/.test(str);
}

export const areDatesEqualUTC = (dateA: Date, dateB: Date): boolean => {
    return dateA.toISOString().slice(0, 10) === dateB.toISOString().slice(0, 10);
}

export function convertStringToPoint(point: string): LatLngExpression[] {
    const stringCoordinates = point.split(';')[1];
    const regex = /(-?\d+\.\d+)\s(-?\d+\.\d+)/g;
    const coordinates: LatLngExpression[] = [];

    let match;
    while ((match = regex.exec(stringCoordinates))) {
        const lng = match[1];
        const lat = match[2];
        coordinates.push([parseFloat(lat), parseFloat(lng)]);
    }

    return coordinates;
}

// Interface for preserving polygon structure in MULTIPOLYGON
export interface MultiPolygonData {
    polygons: LatLngExpression[][][]; // Array of polygons, each containing rings (exterior + holes)
}

export function convertStringToShape(shape: string): LatLngExpression[][][] {
    if (!shape) return [[[]]];
    
    // Parse the shape and preserve polygon structure
    const multiPolygonData = parseMultiPolygonString(shape);
    
    // Return the original structure without flattening
    return multiPolygonData.polygons.length > 0 ? multiPolygonData.polygons : [[[]]];
}

export function parseMultiPolygonString(shape: string): MultiPolygonData {
    if (!shape) return { polygons: [[[]]] };
    
    // Remove SRID prefix if present
    const wktString = shape.includes(';') ? shape.split(';')[1] : shape;
    
    // Check if it's a MULTIPOLYGON
    const multiPolygonRegex = /MULTIPOLYGON\s*\(\s*(.+)\s*\)/i;
    const multiPolygonMatch = multiPolygonRegex.exec(wktString);
    
    if (multiPolygonMatch) {
        const content = multiPolygonMatch[1];
        const polygons: LatLngExpression[][][] = [];
        
        // Split by main polygon groups - each group is wrapped in double parentheses
        const polygonGroups = splitPolygonGroups(content);
        
        for (const group of polygonGroups) {
            // Parse individual rings within this polygon group
            const rings = parsePolygonRings(group);
            if (rings.length > 0) {
                polygons.push(rings);
            }
        }
        
        return { polygons: polygons.length > 0 ? polygons : [[[]]] };
    }
    
    // Fallback to POLYGON format for backward compatibility
    const polygonRegex = /POLYGON\s*\(\((.*)\)\)/i;
    const polygonMatch = polygonRegex.exec(wktString);
    
    if (!polygonMatch) return { polygons: [[[]]] };
    
    const rings = parsePolygonRings(polygonMatch[1]);
    return { polygons: rings.length > 0 ? [rings] : [[[]]] };
}

// Helper function to split polygon groups in MULTIPOLYGON
function splitPolygonGroups(content: string): string[] {
    const groups: string[] = [];
    let depth = 0;
    let start = -1;
    for (let i = 0; i < content.length; i++) {
        if (content[i] === '(') {
            if (depth === 0) {
                start = i + 1;
            }
            depth++;
        } else if (content[i] === ')') {
            depth--;
            if (depth === 0 && start !== -1) {
                // Found a complete polygon group
                const group = content.substring(start, i);
                if (group.trim()) {
                    groups.push(group);
                }
                start = -1;
            }
        }
    }
    
    return groups;
}

// Helper function to parse rings within a polygon
function parsePolygonRings(polygonContent: string): LatLngExpression[][] {
    const rings: LatLngExpression[][] = [];
    
    // If the content has parentheses, it means we have multiple rings (exterior + holes)
    if (polygonContent.includes('(')) {
        let depth = 0;
        let start = -1;
        
        for (let i = 0; i < polygonContent.length; i++) {
            if (polygonContent[i] === '(') {
                if (depth === 0) start = i + 1;
                depth++;
            } else if (polygonContent[i] === ')') {
                depth--;
                if (depth === 0 && start !== -1) {
                    const ringStr = polygonContent.substring(start, i);
                    const coordinates = parseCoordinates(ringStr);
                    if (coordinates.length > 0) {
                        rings.push(coordinates);
                    }
                    start = -1;
                }
            }
        }
    } else {
        // Single ring without parentheses
        const coordinates = parseCoordinates(polygonContent);
        if (coordinates.length > 0) {
            rings.push(coordinates);
        }
    }
    
    return rings;
}

// Helper function to parse coordinate pairs
function parseCoordinates(coordinateStr: string): [number, number][] {
    const coordinates: [number, number][] = [];
    const regex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;
    let match;
    
    while ((match = regex.exec(coordinateStr))) {
        const lng = parseFloat(match[1]);
        const lat = parseFloat(match[2]);
        coordinates.push([lat, lng]);
    }

    // Elimina el último punto si es igual al primero (solo si ambos son arrays)
    if (
        coordinates.length > 1 &&
        coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
        coordinates[0][1] === coordinates[coordinates.length - 1][1]
    ) {
        coordinates.pop();
    }
    
    return coordinates;
}


export function parseUrlParameters<
    Params extends { [key: string]: string | number | boolean }
>(uri: string, params: Params): string {
    const keys = Object.keys(params);

    return keys.reduce((uri, parameterName) => {
        return uri.replace(`:${parameterName}`, String(params[parameterName]));
    }, uri);
}

// Función auxiliar para verificar si un punto está dentro de un polígono
const isPointInPolygon = (point: [number, number], polygon: L.LatLngExpression[]): boolean => {
    const [lat, lng] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        // Convertir el punto del polígono al formato correcto
        let xi: number, yi: number, xj: number, yj: number;
        
        // Manejar diferentes formatos de coordenadas
        if (Array.isArray(polygon[i])) {
            [xi, yi] = polygon[i] as [number, number];
        } else {
            const pointI = polygon[i] as any;
            xi = pointI.lat || pointI[0];
            yi = pointI.lng || pointI[1];
        }
        
        if (Array.isArray(polygon[j])) {
            [xj, yj] = polygon[j] as [number, number];
        } else {
            const pointJ = polygon[j] as any;
            xj = pointJ.lat || pointJ[0];
            yj = pointJ.lng || pointJ[1];
        }
        
        if (((yi > lng) !== (yj > lng)) && (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    
    return inside;
};

// Función auxiliar para verificar si un polígono está completamente dentro de otro
export const isPolygonInside = (innerPolygon: L.LatLngExpression[], outerPolygon: L.LatLngExpression[]): boolean => {
    // Verificar que todos los puntos del polígono interior estén dentro del exterior
    for (const point of innerPolygon) {
        let lat: number, lng: number;
        
        // Manejar diferentes formatos de coordenadas
        if (Array.isArray(point)) {
            [lat, lng] = point as [number, number];
        } else {
            const pointObj = point as any;
            lat = pointObj.lat || pointObj[0];
            lng = pointObj.lng || pointObj[1];
        }
        
        if (!isPointInPolygon([lat, lng], outerPolygon)) {
            return false;
        }
    }
    return true;
};

export const translationDic = (key: string) => {

    if (key.includes('_HAS')) {
        return 'PRODUCTIVE_UNIT.HECTARES'
    }
    return ''
}


export const isTechnicalChief = (user: User|null) => {
    if (user === null) {
        return false;
    }
    if (user.divisions === undefined) {
        return false;
    }
    for (const division of user.divisions) {
        if (division.is_technician_chief) {
            return true;
        }
    }
}

export const getStageDesc = (code: string) => {
    switch (code) {
        case "PRE":
            return "PRODUCTIVE_UNIT.STAGE_1"
        case "SIE":
            return "PRODUCTIVE_UNIT.STAGE_2"
        case "DES":
            return "PRODUCTIVE_UNIT.STAGE_3"
        case "FLO":
            return "PRODUCTIVE_UNIT.STAGE_4"
        case "COS":
            return "PRODUCTIVE_UNIT.STAGE_5"
        case "FCO":
            return "PRODUCTIVE_UNIT.STAGE_6"
        case "CER":
            return "PRODUCTIVE_UNIT.STAGE_FINAL"
        default:
            return "PRODUCTIVE_UNIT.STAGE_UNKNOWN"
    }
}

export const getTypeEnvironmentDesc = (code: string) => {
    const descriptions = {
        "GEN": "ENVIRONMENT.GENERAL",
        "PAR": "ENVIRONMENT.PARTICULAR",
        "TEM": "ENVIRONMENT.TEMPORAL",
        "SIE": "ENVIRONMENT.SOWN",
        "PRI": "ENVIRONMENT.PRINCIPAL"
    };
    return descriptions[code as keyof typeof descriptions] || "ENVIRONMENT.PRINCIPAL";
};
export const kmlToMultiPolygonWKT = (kmlDoc: XMLDocument): string => {
    const geojson = kml(kmlDoc);
    const polygons: number[][][][] = [];

    if ('features' in geojson) {
        geojson.features.forEach((feature) => {
            if (feature.geometry) {
                // Manejar GeometryCollection (MultiGeometry en KML)
                if (feature.geometry.type === 'GeometryCollection') {
                    feature.geometry.geometries.forEach((geom: any) => {
                        if (geom.type === 'Polygon') {
                            polygons.push(geom.coordinates);
                        } else if (geom.type === 'MultiPolygon') {
                            polygons.push(...geom.coordinates);
                        }
                    });
                }
                // Manejar Polygon individual
                else if (feature.geometry.type === 'Polygon') {
                    polygons.push((feature.geometry as Polygon).coordinates);
                }
                // Manejar MultiPolygon
                else if (feature.geometry.type === 'MultiPolygon') {
                    polygons.push(...(feature.geometry as MultiPolygon).coordinates);
                }
            }
        });
    }

    if (polygons.length === 0) return '';

    // Convertir a WKT
    const polygonsWKT = polygons.map((polygon: any[][]) => {
        // Cada polygon es un array de anillos (el primero es el exterior, los siguientes son agujeros)
        const ringsWKT = polygon.map((ring: any[]) => {
            // Cada ring es un array de coordenadas [lng, lat, ...]
            const coords = ring.map(coord => `${coord[0]} ${coord[1]}`).join(', ');
            return `(${coords})`;
        }).join(', ');
        return `(${ringsWKT})`;
    }).join(', ');

    return `SRID=4326;MULTIPOLYGON (${polygonsWKT})`;
};

// Convierte un shape WKT (MULTIPOLYGON) a formato KML
export const wktToKml = (shape: string, name?: string): string => {
    const multiPolygonData = parseMultiPolygonString(shape);
    
    if (!multiPolygonData.polygons || multiPolygonData.polygons.length === 0) {
        return '';
    }
    
    // Convertir de formato Leaflet [lat, lng] a GeoJSON [lng, lat]
    const coordinates = multiPolygonData.polygons.map((polygon) => {
        return polygon.map((ring) => {
            return ring.map((coord) => {
                const [lat, lng] = coord as [number, number];
                return [lng, lat];
            });
        });
    });
    
    const geojson = {
        type: 'FeatureCollection' as const,
        features: [
            {
                type: 'Feature' as const,
                properties: name ? { name } : {},
                geometry: {
                    type: 'MultiPolygon' as const,
                    coordinates: coordinates
                }
            }
        ]
    };
    
    const kmlString = toKML(geojson);
    
    return kmlString;
};

// Calcula el centroide de un shape WKT (MULTIPOLYGON o POLYGON) y retorna en formato SRID=4326;POINT (lng lat)
export const getCentroidFromShapeWKT = (shape: string): string => {
    // Extraer la parte de coordenadas del WKT
    const wktString = shape.includes(';') ? shape.split(';')[1] : shape;
    // Buscar todos los pares lng lat
    const regex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;
    let match;
    let lngSum = 0, latSum = 0, count = 0;
    while ((match = regex.exec(wktString))) {
        const lng = parseFloat(match[1]);
        const lat = parseFloat(match[2]);
        lngSum += lng;
        latSum += lat;
        count++;
    }
    if (count === 0) return '';
    const lngCentroid = lngSum / count;
    const latCentroid = latSum / count;
    return `SRID=4326;POINT (${lngCentroid} ${latCentroid})`;
};
